'use client';

import './radio-group.scss';

import * as RdxRadioGroup from '@radix-ui/react-radio-group';
import {
  FC,
  Fragment,
  type JSX,
  PropsWithChildren,
  ReactNode,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, BadgeProps } from '../../atoms/badge/badge';
import { cn } from '../../utils/helpers';
import { Field, FieldErrorMessage, FieldProps } from '../field/field';

export interface RadioItemProps {
  value: string;
  text?: string;
  disabled?: boolean;
  required?: boolean;
  subText?: string;
  badge?: BadgeProps;
  type?: 'default' | 'other';
  /**
   * These custom radio item elements will be children of the \<label\> element and should be made up of inline elements such as \<span\>. Block elements should not be used.
   *
   * For accessibility, you should not place interactive content such as anchors or buttons in your provided radio item element.
   *
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/label | the Label element on MDN}
   */
  element?: JSX.Element;
  // autoFocus?: boolean;
}

const RadioGroupItem: FC<
  PropsWithChildren<{
    id: string;
    currentValue: string;
    item: RadioItemProps;
    className?: string;
    isFocused: boolean | undefined;
    altNode?: ReactNode;
  }>
> = ({ id, currentValue, item, className, isFocused, altNode, children }) => {
  const initiatedFocus = useRef(false);

  return (
    <>
      <RdxRadioGroup.Item
        ref={(node: HTMLElement | null) => {
          // This is a bit of a hack...
          if (
            isFocused &&
            !initiatedFocus.current &&
            currentValue === item.value
          ) {
            // @ts-expect-error TS isn't smart enough to know that we'll always have a node if above conditions are true.
            node.focus();
            initiatedFocus.current = true;
          }
        }}
        id={id}
        className={className}
        value={item.value}
        disabled={item.disabled}
        aria-label={item.text || item.value}
      >
        {children}
        <RdxRadioGroup.Indicator className="RadioGroup__indicator" />
      </RdxRadioGroup.Item>
      {altNode}
    </>
  );
};

export interface RadioGroupProps extends Omit<
  FieldProps,
  | 'children'
  | 'min'
  | 'max'
  | 'minLength'
  | 'maxLength'
  | 'valueAsNumber'
  | 'valueAsDate'
  | 'pattern'
> {
  label?: string;
  variant?: 'default' | 'buttons' | 'boxed' | 'grid';
  orientation?: RdxRadioGroup.RadioGroupProps['orientation'];
  dir?: RdxRadioGroup.RadioGroupProps['dir'];
  loop?: RdxRadioGroup.RadioGroupProps['loop'];
  'aria-label'?: RdxRadioGroup.RadioGroupProps['aria-label'];
  'aria-labelledby'?: RdxRadioGroup.RadioGroupProps['aria-labelledby'];
  'aria-describedby'?: RdxRadioGroup.RadioGroupProps['aria-describedby'];
  'aria-details'?: RdxRadioGroup.RadioGroupProps['aria-details'];
  'aria-errormessage'?: RdxRadioGroup.RadioGroupProps['aria-errormessage'];
  items?: RadioItemProps[];
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
  label,
  variant = 'default',
  dir,
  loop,
  onChange,
  orientation,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'aria-details': ariaDetails,
  'aria-errormessage': ariaErrorMessage,
  items,
  isFocused,
  defaultValue,
  ...fieldProps
}) => {
  const { t } = useTranslation(['forms']);
  return (
    <Field {...(fieldProps as FieldProps)}>
      {({ controlProps, error, formMethods }) => {
        const handleValueChange = (value: string) => {
          formMethods?.setValue(controlProps.name as string, value);
          const changeEventData = {
            target: { name: controlProps.name, value },
          };
          if (controlProps.onChange) {
            controlProps.onChange(changeEventData);
          }

          if (!onChange) {
            return;
          }

          onChange(changeEventData);
        };

        const labelId = label ? `label-${controlProps.id}` : undefined;
        const currentValue =
          controlProps.value ??
          formMethods?.getValues(controlProps.name || '') ??
          defaultValue ??
          '';

        return (
          <>
            {label && (
              <label
                id={labelId}
                className="RadioGroup__label label-xl label-amplified block mb-comfy-xs"
                htmlFor={controlProps.id}
              >
                {label}
              </label>
            )}
            {/* `controlProps` are only used for RadioGroups used OUTSIDE of a form */}
            <RdxRadioGroup.Root
              className={cn(
                `RadioGroup RadioGroup--${variant}`,
                'flex flex-col',
                {
                  'gap-comfy-sm': variant !== 'boxed',
                },
              )}
              onValueChange={handleValueChange}
              orientation={orientation}
              dir={dir}
              loop={loop}
              aria-label={ariaLabel}
              aria-labelledby={labelId || ariaLabelledBy}
              aria-describedby={ariaDescribedBy}
              aria-details={ariaDetails}
              aria-errormessage={ariaErrorMessage}
              {...(currentValue && {
                value: currentValue,
              })}
              {...(controlProps.defaultValue && {
                defaultValue: controlProps.defaultValue,
              })}
            >
              {items?.map((item) => {
                const id = `${controlProps.id}-${item.value}`;
                return (
                  <Fragment key={id}>
                    {variant === 'grid' && item.type === 'other' && (
                      <div className="RadioGroup__divider-horizontal">
                        <p>{t('radioGroup.or')}</p>
                      </div>
                    )}
                    {variant !== 'boxed' && (
                      <label
                        key={id}
                        className={cn('flex items-center', {
                          'RadioGroup__item-wrapper label-lg label-moderate':
                            !item?.element,
                          'RadioGroup__element-wrapper grow-1': item.element,
                          'RadioGroup__element--other': item.type === 'other',
                          'is-selected':
                            item.type === 'other' &&
                            item.value === currentValue,
                        })}
                        htmlFor={id}
                      >
                        {item.badge && (
                          <div className="RadioGroup__badge">
                            <Badge {...item.badge} />
                          </div>
                        )}
                        <RadioGroupItem
                          id={id}
                          currentValue={currentValue}
                          item={item}
                          className={cn('RadioGroup__item', {
                            'label-lg label-potent': !item.element,
                            'RadioGroup__item--element': item.element,
                            'RadioGroup__item--other': item.type === 'other',
                          })}
                          isFocused={isFocused}
                          altNode={
                            <span
                              aria-hidden={item.type !== 'other'}
                              className="RadioGroup__item-label"
                              role="presentation"
                            >
                              {item.element || item.text}
                            </span>
                          }
                        >
                          <span
                            className={cn('RadioGroup__button-label', {
                              'label-xl label-potent':
                                variant === 'grid' && item.type !== 'other',
                            })}
                          >
                            {item.text || item.value}
                            {item.subText && (
                              <p className={cn('label-mild label-md')}>
                                {item.subText}
                              </p>
                            )}
                          </span>
                        </RadioGroupItem>
                      </label>
                    )}
                    {variant === 'boxed' && (
                      <div className="RadioGroup--boxed__item" key={id}>
                        <RadioGroupItem
                          id={id}
                          currentValue={currentValue}
                          item={item}
                          className="RadioGroup--boxed__item-input"
                          isFocused={isFocused}
                          altNode={
                            <label
                              className="RadioGroup--boxed__item-label relative flex items-center"
                              htmlFor={id}
                            >
                              {item.element || item.text}
                            </label>
                          }
                        />
                      </div>
                    )}
                  </Fragment>
                );
              })}
            </RdxRadioGroup.Root>

            <FieldErrorMessage error={error} id="" />
          </>
        );
      }}
    </Field>
  );
};
